import { createTheme } from "@mui/material/styles";

// Extend Material-UI's theme type to include the custom `atomic` palette
declare module "@mui/material/styles" {
  interface Palette {
    atomic: {
      // Reds
      red5: string;
      red4: string;
      red3: string;
      red2: string;
      red1: string;
      // Greens
      green4: string;
      green3: string;
      green2: string;
      green1: string;
      // Oranges
      orange1: string;
      // Blues
      blue1: string;
      // Purples
      purple1: string;
      // Polish-pass neutrals
      brandBlue: string;
      brandBlueDark: string;
      ink: string;
      inkSecondary: string;
      muted: string;
      faint: string;
      border: string;
      borderLight: string;
      surfaceTint: string;
      panelTint: string;
      // Inflow / outflow band tints + ink
      redBand: string;
      redBand2: string;
      greenBand: string;
      greenBand2: string;
      outflowInk: string;
      inflowInk: string;
    };
  }
  interface PaletteOptions {
    atomic?: {
      // Reds
      red5: string;
      red4: string;
      red3: string;
      red2: string;
      red1: string;
      // Greens
      green4: string;
      green3: string;
      green2: string;
      green1: string;
      // Oranges
      orange1: string;
      // Blues
      blue1: string;
      // Purples
      purple1: string;
      // Polish-pass neutrals
      brandBlue: string;
      brandBlueDark: string;
      ink: string;
      inkSecondary: string;
      muted: string;
      faint: string;
      border: string;
      borderLight: string;
      surfaceTint: string;
      panelTint: string;
      // Inflow / outflow band tints + ink
      redBand: string;
      redBand2: string;
      greenBand: string;
      greenBand2: string;
      outflowInk: string;
      inflowInk: string;
    };
  }
}

const ATOMIC_FONT_STACK =
  '"Archivo", "Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif';

export const CHAT_FONT_STACK =
  '"Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif';

export const theme = createTheme({
  palette: {
    atomic: {
      // Reds
      red5: "#b15d59",
      red4: "#FA7861",
      red3: "#FA9887",
      red2: "#FCBAAE",
      red1: "#f5dada",
      // Greens
      green4: "#CAFA61",
      green3: "#D5FA87",
      green2: "#E4FCAE",
      green1: "#e3edcc",
      // Oranges
      orange1: "#fff7d9",
      // Blues
      blue1: "#e1ecfa", // light blue
      // Purples
      purple1: "#f3e8fd", // new: muted light purple
      // Polish-pass neutrals
      brandBlue: "#3877F7",
      brandBlueDark: "#2A5FDB",
      ink: "#101113",
      inkSecondary: "#5C6066",
      muted: "#8A9099",
      faint: "#B8BDC4",
      border: "#E6E8EC",
      borderLight: "#EFF1F4",
      surfaceTint: "#FAFBFC",
      panelTint: "#F4F6F8",
      // Inflow / outflow band tints + ink
      redBand: "#FAEAE6",
      redBand2: "#F8E7E2",
      greenBand: "#EFF5E4",
      greenBand2: "#EAF1DA",
      outflowInk: "#8E2A1F",
      inflowInk: "#3F6B14",
    },
    mode: "light", // Set to "dark" for dark mode
  },
  typography: {
    h1: {
      fontSize: "1.5rem",
      fontWeight: 400,
      lineHeight: 1.2,
    },
    fontFamily: ATOMIC_FONT_STACK,
    fontSize: 15,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: ATOMIC_FONT_STACK,
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0, // Set default elevation to 0
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
    // Default dropdown font: 12px across the board (closed value + options).
    // Components that need a different size override locally — e.g. the
    // customer selector pins its value/options to 14px.
    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: "12px",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "12px",
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          fontFamily: ATOMIC_FONT_STACK,
          fontSize: "12px",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "8px",
          borderBottom: "1px solid #EFF1F4",
          fontSize: "12px",
          fontFamily: ATOMIC_FONT_STACK,
          fontVariantNumeric: "tabular-nums",
          textWrap: "nowrap",
        },
        head: {
          fontWeight: 600,
          color: "#5C6066",
          backgroundColor: "white",
          fontSize: "11.5px",
          fontFamily: ATOMIC_FONT_STACK,
          borderBottom: "1px solid #E6E8EC",
          textWrap: "nowrap",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          // Subtle hover via brightness so it shows over tinted/highlighted
          // cells too. Excludes thead (tbody scope) and band/group header rows.
          "tbody &:not(.atomic-band-row):hover td": {
            filter: "brightness(0.965)",
          },
        },
      },
    },
  },
});
